import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { STATE_GRID, PROVINCE_GRID, CA_ROWS } from '../constants/stateGrid';
import { AdminSidebar, personTarget } from '../components/AdminShell';
import GuardianLinksModal from '../components/GuardianLinksModal';
import NeedsActionStrip from '../components/admin/NeedsActionStrip';
import { DashTabs, TabPanel, useDashTab } from '../components/admin/DashTabs';
import './AdminDashboard.css';

const WINDOWS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
];

// One failing tile must not blank the dashboard — the same defensive shape the
// old admin branch used for its report fetches (DashboardPage.js:523).
const safe = (p) => p.then((r) => r.data).catch(() => null);

function useAdminDashboardData() {
  const { API_URL } = useAuth();
  // Derived from API_URL per the repo's convention (DashboardPage.js:540,
  // Layout.js:31, AdminFlagsPage.js:8), not a separate REACT_APP_* env var.
  const BILLING_URL = `${API_URL.replace('/api', '')}/billing`;
  const MESSAGING_URL = `${API_URL.replace('/api', '')}/messaging`;

  const [window_, setWindow] = useState('7d');
  const [series, setSeries] = useState(null);
  const [payments, setPayments] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [statics, setStatics] = useState({});
  const [loading, setLoading] = useState(true);

  // Window-dependent: refetched on every switch.
  useEffect(() => {
    let cancelled = false;
    safe(axios.get(`${API_URL}/admin/reports/dashboard-series?window=${window_}`))
      .then((d) => { if (!cancelled) setSeries(d); });
    safe(axios.get(`${API_URL}/admin/reports/payments?window=${window_}`))
      .then((d) => { if (!cancelled) setPayments(d); });
    safe(axios.get(`${API_URL}/admin/reports/active-platforms?window=${window_}`))
      .then((d) => { if (!cancelled) setPlatforms(d); });
    return () => { cancelled = true; };
  }, [window_, API_URL]);

  // Static per load: fetched once. API_URL/BILLING_URL/MESSAGING_URL are
  // derived from useAuth() and stable in value across renders, so including
  // them in the deps array does not cause a second run.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      safe(axios.get(`${API_URL}/admin/reports/user-types`)),
      safe(axios.get(`${API_URL}/admin/reports/athletes-by-state`)),
      safe(axios.get(`${BILLING_URL}/mrr`)),
      safe(axios.get(`${API_URL}/admin/coach-review/pending`)),
      safe(axios.get(`${API_URL}/admin/reports/signups?days=7`)),
      safe(axios.get(`${MESSAGING_URL}/flags/count`)),
      safe(axios.get(`${API_URL}/admin/reports/login-issues`)),
      safe(axios.get(`${API_URL}/admin/merge/candidates/count`)),
    ]).then(([userTypes, states, mrr, pending, signups, flags, issues, candidates]) => {
      if (cancelled) return;
      setStatics({ userTypes, states, mrr, pending, signups, flags, issues, candidates });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [API_URL, BILLING_URL, MESSAGING_URL]);

  return { window: window_, setWindow, series, payments, platforms, loading, ...statics };
}

export function Sparkline({ points, stroke, height = 28 }) {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points, 1);
  const step = 100 / (points.length - 1);
  const d = points
    .map((p, i) => `${i * step},${height - (p / max) * height}`)
    .join(' ');
  return (
    <svg className="adm-spark" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={d} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// A null delta means the prior window had no activity — there is no honest
// percentage for that, so it renders as an em dash rather than +0%.
function DeltaPill({ pct, tone }) {
  const text = pct === null || pct === undefined
    ? '—'
    : `${pct >= 0 ? '+' : ''}${pct}%`;
  return <span className={`adm-pill adm-pill--${tone}`}>{text}</span>;
}

// mrr comes back as `{}` (not null) whenever its fetch resolves without
// error but without the expected shape — e.g. the shared axios mock's
// catch-all in tests. Gate on the field the card actually renders, not on
// object truthiness, or a billing 403 (which is real and expected for
// internal_staff — require_admin_caller only accepts `admin`) degrades to a
// crash instead of an em dash.
function hasMrrData(mrr) {
  return mrr != null && typeof mrr.mrr_dollars === 'number';
}

// Fixed five-source display order (Stripe, Camps, Apple, Google Play, Concierge) shared
// by the revenue KPI card, the by-source card and the transactions table.
// Mirrors backend/app/models/models.py PAYMENT_SOURCES / payment_
// ledger.py's LEDGER_SOURCES — same values, same order, kept in sync by hand
// since the frontend has no import path into that module.
const SOURCE_META = {
  stripe_sub: { label: 'Stripe',      color: '#4D8064' },
  camp:       { label: 'Camps',       color: '#9FC3AE' },
  apple:      { label: 'Apple',       color: '#BC8A3D' },
  google:     { label: 'Google Play', color: '#0D2133' },
  concierge:  { label: 'Concierge',   color: '#8CA096' },
};
const SOURCE_ORDER = ['stripe_sub', 'camp', 'apple', 'google', 'concierge'];

// Two formatters, and which one a figure gets is a decision about what the
// figure IS, not about taste:
//
//   dollars      — AGGREGATES. The revenue headline, the revenue bar's
//                  tooltips, the by-source amounts. Rounding $23,860.14 to
//                  $23,860 loses nothing an admin reads a KPI tile for, and
//                  cents on a five-figure number are noise.
//   dollarsCents — the ROW-BY-ROW transactions table, which is a ledger view
//                  an admin reconciles against Stripe line by line. Rounding
//                  there is not a loss of precision, it is a wrong number:
//                  measured on the committed fixtures, `dollars` renders
//                  940 -> "$9", 999 -> "$10", 849 -> "$8" and 49 -> "$0". A
//                  $9.40 net subscription payment — the actual shape of this
//                  product's revenue — printed "$9", and a $0.49 proration
//                  printed "$0" beside the status "Paid", under a caption
//                  reading "Net proceeds". A confident $0 for a real payment
//                  is the same defect the has_writer work exists to remove,
//                  at row scale.
//
// Both keep the `$-300` sign placement the cards already use, so a refund
// reads the same way wherever it appears.
const dollars = (cents) =>
  `$${Math.round((cents || 0) / 100).toLocaleString('en-US')}`;

const dollarsCents = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Every dollar figure on every payment card is NET proceeds, and the binding
// constraint is that EACH card says so and names the assumed store rate —
// not just the first one. A user reads one card at a time; RevenueCard's
// caption is invisible to someone looking at the transactions table.
//
// One constant, not three literals: this is a single claim about how the
// ledger reports money, and three hand-written phrasings of it would invite
// a reader to hunt for a difference between them that doesn't exist — and
// would let one drift when the commission rate changes.
const NET_PROCEEDS_NOTE =
  'Net proceeds, after 15% store commission on Apple and Google Play.';

// The note above names Apple as a source we take commission on, which reads
// as "Apple is in this number". While Apple has no writer it is NOT — and the
// headline "Revenue this month" is a sum over rows that can never include it.
// A total that silently omits a whole source, under a caption implying it is
// present, is the same failure as a source rendering a confident `$0`; it just
// happens at the number an admin actually reads first.
//
// Derived from the response's own `has_writer` flags rather than hardcoding
// "Apple", so the sentence disappears by itself when Task 7 ships the writer
// and the backend stops sending `has_writer: false` — no second place to
// remember to edit.
// Takes the rows THIS total was computed from, not the whole payload: the
// sentence describes the headline it sits under, and after the scope fix that
// headline is month-scoped. `has_writer` is a fact about the code and reads
// the same in either breakdown, so this is about keeping the caption's subject
// and its evidence the same rows — not a behaviour change.
const unwiredNote = (rows) => {
  const missing = (rows || [])
    .filter(lacksWriter)
    .map((r) => SOURCE_META[r.source]?.label || r.source);
  if (!missing.length) return '';
  return ` Excludes ${missing.join(' and ')} — not wired up yet, so ${
    missing.length > 1 ? 'those sources are' : 'that source is'
  } absent from this total rather than zero in it.`;
};

// The OTHER thing the headline total silently overclaims. `ledger_starts_at`
// was rendered only inside RecentTransactions — the one payment card that
// shows no total at all — while the figure that actually asserts completeness
// is "Revenue this month". The ledger went live with no backfill, so for the
// month it went live in, that headline reports a few days of data under a
// label saying the month. And this card already carries a scope caveat (the
// unwired-source sentence above), which makes the omission read as deliberate
// completeness about everything else.
//
// Conditional on the start date falling INSIDE the month being reported. Once
// the ledger predates the month, "Revenue this month" genuinely is the whole
// month, and a standing caveat would be its own false statement — the same
// defect pointed the other way.
const ledgerNote = (payments) => {
  const iso = payments?.ledger_starts_at;
  if (!iso) return '';
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return '';
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  if (start <= monthStart) return '';
  return ` Ledger begins ${start.toLocaleDateString('en-US')} — nothing before ` +
    `that date is recorded, so this is part of the month, not all of it.`;
};

// One caption, one sentence structure. Both caveats narrow the SAME total in
// the same way — "here is what this number does not contain" — so they are
// appended sentences on the shared NET_PROCEEDS_NOTE rather than a second
// paragraph with its own shape bolted underneath.
const totalScopeNote = (payments, rows) =>
  unwiredNote(rows) + ledgerNote(payments);

// Copy for a source that has no ledger writer at all — `has_writer: false`
// from the payments endpoint (backend payment_ledger.SOURCES_WITH_WRITER).
// It must never be rendered as `$0`: Apple's zero means "nothing can record
// this", Camps' zero means "no camp revenue in this window", and a bare $0
// says the second while meaning the first.
const NO_WRITER_LABEL = 'not wired up';
const noWriterTitle = (label) =>
  `${label}: not wired up yet — nothing records ${label} payments, so this ` +
  `is not a measurement of ${label} revenue. It will read this way ` +
  `regardless of how much ${label} revenue exists.`;

// `has_writer` is absent from an older backend's response. `=== false` (not
// `!== true`) so a missing field falls through to the normal figure rather
// than captioning all four sources as unwired.
const lacksWriter = (row) => row?.has_writer === false;

// Same gating discipline as hasMrrData above: `payments` can be `{}` (a
// thin/unmatched 200 in tests, or a genuinely thin response) which is
// truthy but carries no real figures. Gating on `by_source` being an array
// — a field only the real endpoint ever populates — keeps a fetch failure
// (safe() -> null) or a thin response from rendering a confident "$0" that
// would be indistinguishable from a real, empty ledger.
function hasPaymentsData(payments) {
  return payments != null && Array.isArray(payments.by_source);
}

// BOTH HALVES OF THIS CARD ARE MONTH-SCOPED, and that is a spec requirement,
// not a preference: docs/superpowers/specs/2026-08-26-admin-dashboard-redesign
// -design.md:342 — "Revenue this month — sum of net_cents in the current
// calendar month, with the stacked source bar."
//
// The bar used to read `payments.by_source`, which the endpoint scopes to the
// SELECTED WINDOW (default 7d). On any day past the 7th the bar therefore
// covered a strict subset of the period its own headline covered, and nothing
// on the card said so. It does not blur the composition, it inverts it:
// pushed through the real endpoint with Stripe +$1,000 on the 2nd, Camps +$200
// two days ago and a Stripe refund of -$300 one day ago, the card rendered
// "$900" over a bar reading 100% Camps — Stripe's segment floored to 0% width
// because its 7-day net was negative. The truth was Stripe $700, Camps $200.
// That is the default window, for roughly three weeks in four.
//
// `month_to_date_by_source` is the endpoint's month-scoped breakdown, built
// from the same rows as `month_to_date_net_cents` (payment_ledger.rows_in_month),
// so the segments sum to the headline exactly. The windowed `by_source` stays
// where it belongs — the "Payments by source" card, which says "this window".
function RevenueCard({ payments }) {
  const ok = hasPaymentsData(payments) && Array.isArray(payments.month_to_date_by_source);
  // Gated on the MONTH field, not on `by_source`: falling back to the windowed
  // breakdown when the month one is absent is exactly the bug, and a fallback
  // would reintroduce it silently against an older backend. No bar is an
  // absence; a wrongly-scoped bar is a false statement.
  const rows = ok ? payments.month_to_date_by_source : [];
  const total = rows.reduce((sum, r) => sum + Math.max(r.net_cents, 0), 0);

  return (
    <div className="adm__card">
      <span className="adm-kpi__label adm-kpi__label--dark">Revenue this month</span>
      <div className="adm-kpi__value adm-kpi__value--dark">
        {ok ? dollars(payments.month_to_date_net_cents) : '—'}
      </div>
      <div className="adm-stack">
        {SOURCE_ORDER.map((key) => {
          const row = rows.find((r) => r.source === key);
          const pct = total ? (Math.max(row?.net_cents || 0, 0) / total) * 100 : 0;
          // A missing segment is as much a claim as a labelled one, and this
          // bar is a per-source figure like any other: without this, hovering
          // Apple reads "Apple: $0" — a measured-looking answer for the one
          // source nothing can measure.
          const title = !ok
            ? `${SOURCE_META[key].label}: unavailable`
            : lacksWriter(row)
            ? noWriterTitle(SOURCE_META[key].label)
            : `${SOURCE_META[key].label}: ${dollars(row?.net_cents)}`;
          return (
            <span
              key={key}
              style={{ width: `${pct}%`, background: SOURCE_META[key].color }}
              title={title}
            />
          );
        })}
      </div>
      <div className="adm-kpi__sub">
        {NET_PROCEEDS_NOTE}
        {ok ? totalScopeNote(payments, rows) : ''}
      </div>
    </div>
  );
}

// Growth tab: signups and logins. MRR and the revenue card moved to the Revenue tab.
function GrowthKpis({ series }) {
  const signups = series?.signups;
  const logins = series?.logins;
  return (
    <div className="adm-kpis">
      {/* Signups and logins are the two reports that get opened most, and
          both have had a full page at /admin/signups and /admin/logins since
          they were built -- these tiles just never linked to them, so the
          only route in was the sidebar's Reports tab. The tile IS the link. */}
      <Link to="/admin/signups" className="adm-kpi adm-kpi--hero adm-kpi--link">
        <div className="adm-kpi__top">
          <span className="adm-kpi__label">New signups</span>
          <DeltaPill pct={signups?.delta_pct} tone="onhero" />
        </div>
        <div className="adm-kpi__value">{signups?.total ?? '—'}</div>
        <Sparkline points={signups?.series} stroke="rgba(255,255,255,0.9)" />
        <span className="adm-kpi__go adm-kpi__go--onhero">View report →</span>
      </Link>

      <Link to="/admin/logins" className="adm__card adm-kpi--link">
        <span className="adm-kpi__label adm-kpi__label--dark">Logins</span>
        <div className="adm-kpi__value adm-kpi__value--dark">{logins?.total ?? '—'}</div>
        <div className="adm-kpi__sub">
          {logins ? `${logins.active_today} active today` : ''}
        </div>
        <span className="adm-kpi__go">View report →</span>
      </Link>
    </div>
  );
}

// Revenue tab: MRR and the month's revenue.
function RevenueKpis({ mrr, payments }) {
  const mrrOk = hasMrrData(mrr);
  return (
    <div className="adm-kpis">
      <div className="adm__card">
        <div className="adm-kpi__top">
          <span className="adm-kpi__label adm-kpi__label--dark">MRR</span>
        </div>
        <div className="adm-kpi__value adm-kpi__value--dark">
          {mrrOk ? `$${mrr.mrr_dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
        </div>
        <div className="adm-kpi__sub">
          {mrrOk ? `${mrr.active_count} active subscriptions` : ''}
        </div>
      </div>

      <RevenueCard payments={payments} />
    </div>
  );
}

function SignupsLoginsChart({ series }) {
  const buckets = series?.buckets || [];
  const s = series?.signups?.series || [];
  const l = series?.logins?.series || [];
  const max = Math.max(...s, ...l, 1);

  return (
    <div className="adm__card">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Signups &amp; logins</h2>
        <div className="adm-legend">
          <span className="adm-legend__item"><i style={{ background: 'var(--adm-green)' }} />Signups</span>
          <span className="adm-legend__item"><i style={{ background: 'var(--adm-green-pale)' }} />Logins</span>
        </div>
      </div>
      <div className="adm-chart">
        {buckets.map((label, i) => (
          <div className="adm-chart__group" key={label + i}>
            <div className="adm-chart__bars">
              <span
                className="adm-chart__bar adm-chart__bar--signups"
                style={{ height: `${((s[i] || 0) / max) * 100}%` }}
                title={`${label}: ${s[i] || 0} signups`}
              />
              <span
                className="adm-chart__bar adm-chart__bar--logins"
                style={{ height: `${((l[i] || 0) / max) * 100}%` }}
                title={`${label}: ${l[i] || 0} logins`}
              />
            </div>
            <span className="adm-chart__label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const DONUT = [
  { key: 'paid_athletes',  label: 'Paid athletes',  color: 'var(--adm-green)' },
  { key: 'free_athletes',  label: 'Free athletes',  color: 'var(--adm-green-light)' },
  { key: 'coaches',        label: 'Coaches',        color: 'var(--adm-slate)' },
  { key: 'guardians_plus', label: 'Guardians+',     color: 'var(--adm-amber)' },
];

// The classified population, not every row in the users table: admin,
// internal_staff and marketing accounts fall into none of the four slices
// and are deliberately excluded so the slices stay mutually exclusive and
// sum to `total`. The "TOTAL USERS" caption doesn't say that on its own, so
// the tooltip below is what makes the number auditable against
// /admin/users without changing the design's copy.
const DONUT_TOOLTIP = 'Excludes admin, staff and marketing accounts';

// Shared by the user-types and active-platforms donuts: one stroke-dashed
// circle per slice, laid end to end clockwise from 12 o'clock.
function DonutRing({ slices, data, total, caption, tooltip }) {
  let offset = 25; // start at 12 o'clock
  return (
    <div className="adm-donut">
      <svg viewBox="0 0 42 42" className="adm-donut__svg" aria-hidden="true">
        {slices.map((slice) => {
          const value = data?.[slice.key] || 0;
          const pct = (value / total) * 100;
          const dash = `${pct} ${100 - pct}`;
          const el = (
            <circle
              key={slice.key} cx="21" cy="21" r="15.9"
              fill="transparent" stroke={slice.color} strokeWidth="6"
              strokeDasharray={dash} strokeDashoffset={offset}
            />
          );
          offset -= pct;
          return el;
        })}
      </svg>
      <div className="adm-donut__center" title={tooltip}>
        <div className="adm-donut__total">{total.toLocaleString('en-US')}</div>
        <div className="adm-donut__caption">{caption}</div>
      </div>
    </div>
  );
}

function DonutLegend({ slices, data }) {
  return (
    <ul className="adm-donut__legend">
      {slices.map((slice) => (
        <li key={slice.key}>
          <i style={{ background: slice.color }} />
          <span>{slice.label}</span>
          <strong>{(data?.[slice.key] || 0).toLocaleString('en-US')}</strong>
        </li>
      ))}
    </ul>
  );
}

function UserTypesDonut({ data }) {
  // Gate on `total` specifically, not on `data` truthiness — `data` can be
  // `{}` from the shared catch-all mock/thin 200, and a falsy-but-present
  // total (0) is a real state (no classified users yet), not an error.
  const total = data?.total;
  const hasData = typeof total === 'number' && total > 0;

  if (!hasData) {
    return (
      <div className="adm__card">
        <h2 className="adm-card__title">User types</h2>
        <div className="adm-donut__empty">No user data yet</div>
      </div>
    );
  }

  return (
    <div className="adm__card">
      <h2 className="adm-card__title">User types</h2>
      <DonutRing slices={DONUT} data={data} total={total} caption="TOTAL USERS" tooltip={DONUT_TOOLTIP} />
      <DonutLegend slices={DONUT} data={data} />
      <div className="adm-donut__footer">
        {data?.paid_pct === null || data?.paid_pct === undefined
          ? 'No athletes yet'
          : `${data.paid_pct}% of athletes are on a paid plan`}
      </div>
    </div>
  );
}

const PLATFORM_DONUT = [
  { key: 'ios',     label: 'iOS',     color: 'var(--adm-slate)' },
  { key: 'android', label: 'Android', color: 'var(--adm-green)' },
  { key: 'web',     label: 'Web',     color: 'var(--adm-amber)' },
];

const PLATFORM_TOOLTIP =
  'Users with any tracked activity in the window, excluding admin, staff and marketing accounts';

// Each person is counted once, under the platform they used most, so the
// slices sum to the total. The web app sends no platform header, so its
// slice is logged-in activity that didn't come from either app.
function ActivePlatformsDonut({ data, windowLabel }) {
  const total = data?.total;
  const hasData = typeof total === 'number' && total > 0;
  const title = `Active users · ${windowLabel}`;

  if (!hasData) {
    return (
      <div className="adm__card">
        <h2 className="adm-card__title">{title}</h2>
        <div className="adm-donut__empty">No activity in this window</div>
      </div>
    );
  }

  const multi = data.multi_platform || 0;
  return (
    <div className="adm__card">
      <h2 className="adm-card__title">{title}</h2>
      <DonutRing slices={PLATFORM_DONUT} data={data} total={total} caption="ACTIVE USERS" tooltip={PLATFORM_TOOLTIP} />
      <DonutLegend slices={PLATFORM_DONUT} data={data} />
      <div className="adm-donut__footer">
        {multi === 0
          ? 'Nobody used more than one platform'
          : `${multi.toLocaleString('en-US')} used more than one — counted once, where they're most active`}
      </div>
    </div>
  );
}

// White -> brand green by sqrt(count/max), per the handoff. sqrt rather than
// a linear ramp so mid-sized states stay visible next to an outlier like TX.
// This is the one legitimate hardcoded-color exception in this file: a
// computed numeric ramp can't be expressed as a static var(--adm-*) token.
function tileColor(count, max) {
  const t = max ? Math.sqrt(count / max) : 0;
  const mix = (from, to) => Math.round(from + (to - from) * t);
  return {
    background: `rgb(${mix(255, 63)}, ${mix(255, 107)}, ${mix(255, 83)})`,
    color: t > 0.45 ? '#fff' : '#3A4A41',
  };
}

function StateMap({ data }) {
  const states = data?.states || [];
  const max = data?.max_count || 0;
  const byState = Object.fromEntries(states.map((s) => [s.state, s.count]));
  const top8 = states.slice(0, 8);

  return (
    <div className="adm__card">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Athletes by state / province</h2>
        <span className="adm-card__meta">
          {(data?.total || 0).toLocaleString('en-US')} total
        </span>
      </div>
      <div className="adm-statemap">
        <div className="adm-statemap__grid">
          {[
            ...Object.entries(PROVINCE_GRID),
            ...Object.entries(STATE_GRID).map(([abbr, [row, col]]) => [abbr, [row + CA_ROWS, col]]),
          ].map(([abbr, [row, col]]) => {
            const count = byState[abbr] || 0;
            const style = { gridRow: row, gridColumn: col, ...tileColor(count, max) };
            return (
              <span
                key={abbr}
                className="adm-statemap__tile"
                style={style}
                title={`${abbr}: ${count} athletes`}
              >
                {abbr}
              </span>
            );
          })}
        </div>
        <ul className="adm-statelist">
          {top8.map((s) => (
            <li key={s.state}>
              <span className="adm-statelist__abbr">{s.state}</span>
              <span className="adm-statelist__track">
                <i style={{ width: `${max ? (s.count / max) * 100 : 0}%` }} />
              </span>
              <span className="adm-statelist__count">{s.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PaymentsBySource({ payments, mrr }) {
  const ok = hasPaymentsData(payments);
  const rows = ok ? payments.by_source : [];
  const max = Math.max(...rows.map((r) => Math.max(r.net_cents, 0)), 1);
  const counts = ok ? (payments.status_counts || {}) : {};
  const mrrOk = hasMrrData(mrr);

  return (
    <div className="adm__card">
      <h2 className="adm-card__title">Payments by source</h2>
      <ul className="adm-source">
        {SOURCE_ORDER.map((key) => {
          const row = rows.find((r) => r.source === key);
          const meta = SOURCE_META[key];
          // CLAMP THE GEOMETRY ONLY. `Math.max(net, 0)` is correct for a bar
          // width — a negative bar has no meaning — but the clamped value was
          // reused as the amount TEXT and inside the tooltip, so a source
          // whose refunds exceeded its receipts in the window reported "$0".
          // On the reviewer's payload that put two cards on one screen, in one
          // window, disagreeing about the same money: this card said
          // "Stripe $0" / title "Stripe: $0" while the revenue card's bar said
          // "Stripe: $-300". And $0 is precisely the confident zero the
          // has_writer work exists to eliminate — has_writer does not catch it,
          // because Stripe IS wired.
          //
          // RecentTransactions (`dollars(t.net_cents)`) already renders the
          // negative honestly, so this card was the odd one out in its own
          // file; following the sibling rather than inventing a third
          // behaviour.
          const barValue = Math.max(row?.net_cents || 0, 0);
          const amount = dollars(row?.net_cents);
          // Three distinct states, and the third is the whole point: the
          // fetch failed (unavailable), the source is measured (a figure,
          // possibly an honest zero), or the source has no writer at all
          // (not measurable, and never will be until one ships).
          const unwired = ok && lacksWriter(row);
          const title = !ok
            ? `${meta.label}: unavailable`
            : unwired
            ? noWriterTitle(meta.label)
            : row?.is_derived
            ? `${meta.label}: ${amount} — estimated from list price, not a reported figure`
            : `${meta.label}: ${amount}`;
          return (
            <li key={key} title={title}>
              <span className="adm-source__label">
                {meta.label}
                {ok && !unwired && row?.is_derived && <em className="adm-source__est">est</em>}
              </span>
              <span className="adm-source__track">
                {/* No bar for an unwired source: a zero-width bar next to a
                    real one reads as "measured, and it was nothing". */}
                <i style={{ width: `${ok && !unwired ? (barValue / max) * 100 : 0}%`, background: meta.color }} />
              </span>
              <span className={`adm-source__amt${unwired ? ' adm-source__amt--unwired' : ''}`}>
                {!ok ? '—' : unwired ? NO_WRITER_LABEL : amount}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="adm-source__divider" />
      <div className="adm-source__mrr">
        <span className="adm-source__mrrlabel">MRR</span>
        <span className="adm-source__mrrvalue">
          {mrrOk ? `$${mrr.mrr_dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
        </span>
      </div>

      <div className="adm-source__pills">
        {/* --red-onlight, not --red: `.adm-pill--red` is a dark-card pill
            (its only other user is FlagsCard, `.adm__card--dark`). Its
            #FCA5A5-on-rgba(192,57,43,0.25) pairing measures 7.31:1 there and
            1.29:1 here on a white `.adm__card` — pale pink on pale pink, so
            the count of payments that did not arrive could not be read at
            all. The variant measures 4.55:1. See AdminDashboard.css. */}
        <span className="adm-pill adm-pill--red-onlight">{ok ? (counts.failed || 0) : '—'} failed</span>
        {!ok ? (
          // The fetch never resolved into usable data, so we do not know
          // whether refunds are tracked. "refunds not tracked" asserts a
          // specific fact — that charge.refunded is not subscribed — and
          // this render has no evidence for it either way. Same shape as the
          // "—" the other figures on this card fall back to.
          <span className="adm-pill adm-pill--muted">refunds unavailable</span>
        ) : payments.refunds_tracked ? (
          <span className="adm-pill adm-pill--amber">{counts.refunded || 0} refunded</span>
        ) : (
          // Not "0 refunded" — the charge.refunded webhook may not be
          // subscribed, and 0 would assert something we cannot know. This
          // is the LIVE state today (owner has deliberately not enabled it).
          <span className="adm-pill adm-pill--muted">refunds not tracked</span>
        )}
        <span className="adm-source__window">this window</span>
      </div>

      <p className="adm-txn__note">{NET_PROCEEDS_NOTE}</p>
    </div>
  );
}

// `pending` is a flat array from /admin/coach-review/pending when the fetch
// succeeds, or null when safe() catches a rejection — but under a thin/
// catch-all 200 (e.g. the shared test mock's default `{ data: {} }`) it can
// resolve to a truthy, non-array object. `(pending || []).slice(...)` would
// pass that object straight through and crash on `.slice is not a
// function`, which is exactly the "thin 200 sails through" bug class this
// file has already been bitten by twice for object-truthiness gating — so
// this gates on Array.isArray, the actual shape needed, not on truthiness.
function ApprovalsCard({ pending }) {
  const list = Array.isArray(pending) ? pending : [];
  const rows = list.slice(0, 5);
  return (
    <div className="adm__card">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Coach approvals</h2>
        <span className="adm-pill adm-pill--amber">{list.length} pending</span>
      </div>
      <ul className="adm-list">
        {rows.map((c) => (
          <li key={c.id}>
            <span className="adm-list__name">{c.first_name} {c.last_name}</span>
            <span className="adm-list__meta">{c.school || c.email}</span>
          </li>
        ))}
      </ul>
      <Link className="adm-card__link" to="/admin/users?approval=pending">Review queue</Link>
    </div>
  );
}

// `count` must be the caller's `flags?.pending_flags` — messaging-service's
// GET /flags/count (messaging-service/app/main.py:2113-2124) returns
// `{ pending_flags }`, not `{ count }`. Layout.js:165 already reads this
// correctly; this component was shipped reading the wrong field (`flags?.
// count`, always undefined) until a review caught it — the card silently
// showed "0 open" while real flags piled up, because every test fixture
// happened to mock the shape the code expected rather than the shape the
// endpoint actually returns.
function FlagsCard({ count, latest }) {
  return (
    <div className="adm__card adm__card--dark">
      <div className="adm-card__head">
        <h2 className="adm-card__title adm-card__title--light">Flagged messages</h2>
        <span className="adm-pill adm-pill--red">{count || 0} open</span>
      </div>
      <p className="adm-flags__latest">{latest || 'Nothing flagged right now.'}</p>
      <Link className="adm-card__link adm-card__link--light" to="/admin/flags">Open queue</Link>
    </div>
  );
}

function initials(first, last) {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase() || '?';
}

function relativeTime(iso) {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
}

// /admin/reports/signups serializes `role` via coalesce_role()
// (backend/app/services/roles.py), which normalizes base_user/player_parent
// to "athlete" — so "athlete" is the value this card actually receives, not
// "player_parent".
const ROLE_LABEL = {
  athlete: 'Athlete', coach: 'Coach', basic_coach: 'Coach',
  guardian: 'Guardian', partner: 'Partner',
  admin: 'Admin', internal_staff: 'Staff', marketing: 'Marketing',
};

const STATUS_LABEL = { paid: 'Paid', failed: 'Failed', refunded: 'Refunded' };

function RecentTransactions({ payments }) {
  const ok = hasPaymentsData(payments);
  const rows = ok ? (payments.recent || []) : [];

  return (
    <div className="adm__card">
      <h2 className="adm-card__title">Recent transactions</h2>
      {!ok ? (
        // Distinct from the empty-ledger case below: this means the fetch
        // itself never resolved into usable data (failed, or a thin/
        // unmatched response), not that the window genuinely has zero rows.
        <p className="adm-txn__empty">Unable to load recent transactions.</p>
      ) : rows.length === 0 ? (
        <p className="adm-txn__empty">No transactions in this window.</p>
      ) : (
        <div className="adm-txn">
          <div className="adm-txn__head">
            <span>Name</span><span>Source</span><span>Amount</span>
            <span>Status</span><span>When</span>
          </div>
          {rows.map((t) => (
            <div className="adm-txn__row" key={t.provider_txn_id}>
              <span className="adm-txn__name">{t.name}</span>
              <span>
                {SOURCE_META[t.source]?.label || t.source}
                {t.price_source === 'derived' && (
                  <em className="adm-source__est" title="Estimated from list price">est</em>
                )}
              </span>
              {/* dollarsCents, not dollars: this is a row-by-row ledger view
                  an admin reconciles against Stripe, and whole-dollar
                  rounding here prints wrong numbers, not rounded ones —
                  940 -> "$9", 999 -> "$10", 49 -> "$0" beside "Paid". The
                  aggregate cards keep whole dollars; see the formatters. */}
              <span>{dollarsCents(t.net_cents)}</span>
              <span>
                <em className={`adm-status adm-status--${t.event_type}`}>
                  {STATUS_LABEL[t.event_type] || t.event_type}
                </em>
              </span>
              <span className="adm-txn__when">{relativeTime(t.occurred_at)}</span>
            </div>
          ))}
        </div>
      )}
      {/* Unconditional, unlike the ledger-start note below: the Amount
          column is net-of-commission whether or not there are rows to show,
          and this card is read on its own. */}
      <p className="adm-txn__note">{NET_PROCEEDS_NOTE}</p>
      {ok && payments.ledger_starts_at && (
        <p className="adm-txn__note">
          Ledger begins {new Date(payments.ledger_starts_at).toLocaleDateString('en-US')}.
          Apple and Google Play history starts then; earlier purchases are not recorded.
        </p>
      )}
    </div>
  );
}

function RecentSignupsCard({ signups }) {
  const navigate = useNavigate();
  const [guardianModal, setGuardianModal] = useState(null);
  const rows = (signups?.signups || []).slice(0, 6);

  const open = (u) => {
    const target = personTarget(u.role);
    if (target === 'profile') navigate(`/athletes/${u.id}`);
    else if (target === 'guardian') setGuardianModal(u);
  };

  return (
    <div className="adm__card">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Recent signups</h2>
        <Link className="adm-card__link" to="/admin/signups">View all</Link>
        <Link className="adm-card__link" to="/admin/client-proof">Client proof</Link>
      </div>
      {rows.length === 0 ? (
        <p className="adm-txn__empty">No signups in the last 7 days.</p>
      ) : (
        <ul className="adm-signups">
          {rows.map((u) => {
            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
            const target = personTarget(u.role);
            const meta = (
              <>
                <span className="adm-signups__avatar">
                  {initials(u.first_name, u.last_name)}
                </span>
                <span className="adm-signups__body">
                  <span className="adm-list__name">{name}</span>
                  <span className="adm-list__meta">
                    {ROLE_LABEL[u.role] || u.role}
                    {u.user_source ? ` · ${u.user_source}` : ''}
                  </span>
                </span>
                <span className="adm-signups__when">{relativeTime(u.created_at)}</span>
              </>
            );
            return (
              <li key={u.id}>
                {target ? (
                  <button
                    type="button"
                    className="adm-signups__row adm-signups__row--link"
                    onClick={() => open(u)}
                    title={target === 'guardian'
                      ? `View athletes linked to ${name}`
                      : `View ${name}'s full profile`}
                  >
                    {meta}
                  </button>
                ) : (
                  <div className="adm-signups__row">{meta}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <GuardianLinksModal
        guardian={guardianModal}
        onClose={() => setGuardianModal(null)}
        onOpenAthlete={(athleteId) => { setGuardianModal(null); navigate(`/athletes/${athleteId}`); }}
      />
    </div>
  );
}

export function WindowSwitcher({ value, onChange }) {
  return (
    <div className="adm-window" role="group" aria-label="Time window">
      {WINDOWS.map((w) => (
        <button
          key={w.key}
          type="button"
          className={`adm-window__btn ${value === w.key ? 'adm-window__btn--active' : ''}`}
          onClick={() => onChange(w.key)}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function AdminDashboard({ user }) {
  const today = new Date();
  const dateLine = today.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  const {
    window: activeWindow, setWindow, series, payments, platforms, mrr, userTypes,
    states, pending, signups, flags, issues, candidates,
  } = useAdminDashboardData();
  const [tab, setTab] = useDashTab();
  const approvalsCount = Array.isArray(pending) ? pending.length : 0;

  return (
    <div className="adm">
      <div className="adm__shell">
        <AdminSidebar active="overview" approvalsCount={approvalsCount} flagsCount={flags?.pending_flags || 0} />
        <main className="adm__main">
          <header className="adm-header">
            <div>
              <h1 className="adm-header__title">
                {greeting(today)}, {user?.first_name}
              </h1>
              <p className="adm-header__sub">
                Here's how ProZpects is doing — {dateLine}
              </p>
            </div>
            <WindowSwitcher value={activeWindow} onChange={setWindow} />
          </header>
          <NeedsActionStrip pending={pending} flags={flags} payments={payments} issues={issues} candidates={candidates} />
          <DashTabs value={tab} onChange={setTab} />
          <TabPanel name="growth" value={tab}>
            <GrowthKpis series={series} />
            <div className="adm-row adm-row--5-3-3">
              <SignupsLoginsChart series={series} />
              <UserTypesDonut data={userTypes} />
              <ActivePlatformsDonut
                data={platforms}
                windowLabel={WINDOWS.find((w) => w.key === activeWindow)?.label}
              />
            </div>
          </TabPanel>
          <TabPanel name="revenue" value={tab}>
            <RevenueKpis mrr={mrr} payments={payments} />
            <div className="adm-row adm-row--3-2">
              <PaymentsBySource payments={payments} mrr={mrr} />
              <RecentTransactions payments={payments} />
            </div>
          </TabPanel>
          <TabPanel name="audience" value={tab}>
            <div className="adm-row adm-row--3-2">
              <StateMap data={states} />
              <RecentSignupsCard signups={signups} />
            </div>
            <div className="adm-row adm-row--2-1">
              <ApprovalsCard pending={pending} />
              <FlagsCard count={flags?.pending_flags} />
            </div>
          </TabPanel>
        </main>
      </div>
    </div>
  );
}
